/**
 * AuthCallback — processes the Emergent-managed Google OAuth fragment.
 *
 * Lives at every route via the synchronous fragment-detection in App.js. It
 * exchanges the one-time session_id for a persistent session_token cookie
 * via POST /api/auth/google-session, then navigates the user to the
 * `returnTo` path that was stashed before the redirect (default /admin).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { exchangeSession } from "../lib/auth";

export default function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const processedRef = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (processedRef.current) return;
        processedRef.current = true;
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;
        const returnTo = sessionStorage.getItem("auth_return_to") || "/admin";
        if (!sessionId) {
            setError("No session id in callback URL.");
            return;
        }
        (async () => {
            try {
                const user = await exchangeSession(sessionId);
                sessionStorage.removeItem("auth_return_to");
                // Clear the fragment so refresh doesn't re-run this handler.
                window.history.replaceState({}, "", window.location.pathname);
                navigate(returnTo, { replace: true, state: { user } });
            } catch (e) {
                setError(
                    e?.response?.data?.detail ||
                        "Could not complete sign-in. Please try again."
                );
            }
        })();
    }, [navigate, location]);

    return (
        <div
            className="min-h-screen flex items-center justify-center px-6 text-center"
            data-testid="auth-callback"
        >
            <div className="codex-card max-w-md w-full px-6 py-8">
                {error ? (
                    <>
                        <p className="font-mono-os text-[10px] tracking-[0.36em] uppercase text-[#F43F5E]">
                            Sign-in failed
                        </p>
                        <p className="mt-3 text-[#F1F5F9]">{error}</p>
                        <button
                            onClick={() => navigate("/admin", { replace: true })}
                            className="codex-button mt-5"
                        >
                            Try again
                        </button>
                    </>
                ) : (
                    <>
                        <p className="font-mono-os text-[10px] tracking-[0.36em] uppercase text-[#D4AF37]">
                            Verifying
                        </p>
                        <p className="mt-3 font-serif-display text-[#F9E596] text-2xl">
                            Opening the Codex…
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
